import { useEffect, useState } from 'react';
import { FormProvider, SubmitHandler, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { DynamicInputFields } from '@components/dynamic-input-fields';
import { Field } from '@components/dynamic-input-fields/src/dynamic-input-fields.type';
import { REQUIRED_TEXT_ERROR } from '@components/dynamic-input-fields/src/utils';
import { ErrorMessage } from '@components/error/src/error-message';
import { LabelPrimary } from '@components/label';
import { Select, SelectOption } from '@components/select';
import { filterCollectedFormFields } from '@utils/collected-form-fields';

import { ModalStickyFooter } from '../../modal-sticky-footer';
import { ModalStickyHeader } from '../../modal-sticky-header';
import { ModalV2 } from '../../modal-v2';

import { AddHolderModalProps } from './add-holder-modal.type';
import { createDefaultValues, ensureEmailField, mapDataToBackend } from './utils';

export const AddHolderModal = ({
  isOpen,
  selectedPass: { id: passId, ticketHolderFormFields, bookingFormFields, settings },
  venueIsoCode,
  defaultChannelId,
  preserveModal = false,
  title,
  channels,
  buttonPrimaryText,
  buttonSecondaryText,
  loadingChannels,
  handleClose,
  handleOnCheckboxChange,
  handlePrimaryButtonClick,
  handleSecondaryButtonClick,
}: AddHolderModalProps) => {
  const { t } = useTranslation();
  const [addAnotherHolderCheckbox, setAddAnotherHolderCheckbox] = useState(preserveModal);
  const requiredText = t(REQUIRED_TEXT_ERROR);
  const collectedBookingFields = filterCollectedFormFields(bookingFormFields);
  const fields = settings.isNominative
    ? ensureEmailField(filterCollectedFormFields(ticketHolderFormFields), collectedBookingFields)
    : collectedBookingFields;
  const defaultValues = createDefaultValues(fields, venueIsoCode, defaultChannelId);
  const methods = useForm<Record<string, string>>({
    mode: 'onChange',
    defaultValues,
  });

  const {
    formState: { errors, isSubmitSuccessful, isValid },
    watch,
    handleSubmit,
    register,
    reset,
  } = methods;

  const watchChannelId = watch('channelId');

  const handleCheckboxChange = () => {
    setAddAnotherHolderCheckbox(!addAnotherHolderCheckbox);
    if (handleOnCheckboxChange) handleOnCheckboxChange(!addAnotherHolderCheckbox);
  };

  const onSubmit: SubmitHandler<Record<string, string>> = (data: Record<string, string>) => {
    handlePrimaryButtonClick(mapDataToBackend(data, passId));
  };

  useEffect(() => {
    if (isSubmitSuccessful) reset(defaultValues);
  }, [isSubmitSuccessful, reset, defaultValues]);

  return (
    <ModalV2 isOpen={isOpen} onClose={handleClose} variant='md'>
      <form className='flex justify-end flex-col' onSubmit={handleSubmit(onSubmit)}>
        <ModalStickyHeader handleClose={handleClose} title={title} />
        <div className='py-8 px-6'>
          <FormProvider {...methods}>
            <div className='mb-4'>
              <LabelPrimary htmlFor='sales-channel'>{t('passes:addHolderModalSelectChannelsLabel')}</LabelPrimary>
              {!loadingChannels && (
                <>
                  <Select
                    {...register('channelId', { required: requiredText })}
                    hasError={!!errors.channelId}
                    isEmpty={!watchChannelId}
                    selectFontLight={true}>
                    <SelectOption value={''} label={t('passes:addHolderModalSelectChannelPlaceholder')} />
                    {channels.map((channel) => (
                      <SelectOption value={channel.id} label={channel.name} key={channel.id} />
                    ))}
                  </Select>
                  <ErrorMessage name={'channelId'} />
                </>
              )}
            </div>
            <DynamicInputFields fields={fields as Field[]} venueIsoCode={venueIsoCode} />
          </FormProvider>
        </div>
        <ModalStickyFooter
          isSubmit
          primaryButtonText={buttonPrimaryText}
          isSubmitDisabled={!isValid}
          secondaryButtonText={buttonSecondaryText}
          onSecondaryButtonClick={handleSecondaryButtonClick}
          tertiaryButtonText={t('passes:addHolderModalCheckboxLabel')}
          // The tertiary button owns the toggle: its checkbox is decorative —
          // read-only, out of the tab order and hidden from assistive tech.
          tertiaryIcon={
            <input type='checkbox' checked={addAnotherHolderCheckbox} readOnly tabIndex={-1} aria-hidden='true' />
          }
          onTertiaryButtonClick={handleCheckboxChange}
        />
      </form>
    </ModalV2>
  );
};
